import { useState, useEffect } from 'react';
import { Link2, CheckCircle, AlertCircle, Loader2, ExternalLink, Check, ChevronDown, ChevronUp, Plus, Trash2, BookOpen, GraduationCap, Settings } from 'lucide-react';
import { getUser, authenticatedFetch } from '../lib/auth';
import ReactMarkdown from 'react-markdown';
import { useConfirm } from './ui';
import MoodleSettings from './settings/MoodleSettings';
import TutorLMSSettings from './settings/TutorLMSSettings';
import LearnDashSettings from './settings/LearnDashSettings';

interface Integration {
  id: string;
  member_id: string;
  has_api_key: boolean;
  connection_tested: boolean;
  connection_tested_at: string | null;
  connection_test_error: string | null;
  agreement_confirmed: boolean;
  agreement_confirmed_at: string | null;
  agreement_accepted: boolean;
  agreement_accepted_at: string | null;
  quality_control_confirmed: boolean;
  content_alignment_confirmed: boolean;
  assessment_guidelines_confirmed: boolean;
  spot_check_allowed: boolean;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  total_synced_completions: number;
  total_certificates_issued: number;
  approved_by_forbund: boolean;
}

interface AgreementTemplates {
  [key: string]: {
    name: string;
    content: string;
  };
}

interface XtramileCourse {
  id: string;
  name: string;
  description: string;
  url: string;
  isMapped: boolean;
}

interface CourseMapping {
  id: string;
  xtramile_course_id: string;
  xtramile_course_name: string;
  xtramile_course_url: string;
  study_plan_id: string;
  study_plan_title: string;
  validation_status: string;
  is_active: boolean;
  created_at: string;
}

interface StudyPlan {
  id: string;
  title: string;
  status: string;
  approval_link: string;
}

export default function MemberIntegrations() {
  const { confirm } = useConfirm();
  const user = getUser();
  const [activeLms, setActiveLms] = useState<'xtramile' | 'moodle' | 'tutorlms' | 'learndash'>('xtramile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [integration, setIntegration] = useState<Integration | null>(null);
  const [templates, setTemplates] = useState<AgreementTemplates>({});
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Agreement confirmations
  const [confirmations, setConfirmations] = useState({
    dataAgreement: false,
    qualityControl: false,
    contentAlignment: false,
    assessmentGuidelines: false,
    spotCheckAllowed: false
  });

  // Expanded agreements (for viewing after confirmation)
  const [expandedAgreements, setExpandedAgreements] = useState<string[]>([]);

  // Show config section when fully configured (collapsed by default)
  const [showConfigSection, setShowConfigSection] = useState(false);

  // Course mapping state
  const [courses, setCourses] = useState<XtramileCourse[]>([]);
  const [mappings, setMappings] = useState<CourseMapping[]>([]);
  const [studyPlans, setStudyPlans] = useState<StudyPlan[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [showMappingForm, setShowMappingForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedStudyPlan, setSelectedStudyPlan] = useState<string>('');
  const [savingMapping, setSavingMapping] = useState(false);

  useEffect(() => {
    loadData();
  }, [user?.member_id]);

  const loadData = async () => {
    if (!user?.member_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Load integration and templates in parallel
      const [integrationRes, templatesRes] = await Promise.all([
        authenticatedFetch(`/api/xtramile/integrations/member/${user.member_id}`),
        authenticatedFetch('/api/xtramile/agreement-templates')
      ]);

      const integrationData = await integrationRes.json();
      const templatesData = await templatesRes.json();

      setIntegration(integrationData.integration);
      setTemplates(templatesData.templates || {});

      // Pre-fill confirmations if already confirmed
      if (integrationData.integration?.agreement_confirmed) {
        setConfirmations({
          dataAgreement: true,
          qualityControl: true,
          contentAlignment: true,
          assessmentGuidelines: true,
          spotCheckAllowed: true
        });
      }

    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!user?.member_id || !apiKey.trim()) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/xtramile/integrations/member/${user.member_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: apiKey.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke lagre API-nøkkel');
      }

      setSuccess('API-nøkkel lagret');
      setApiKey('');
      await loadData();

    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!integration?.id) return;

    try {
      setTesting(true);
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/xtramile/integrations/${integration.id}/test`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success && data.connected) {
        setSuccess(data.message);
      } else {
        setError(data.message || 'Tilkobling feilet');
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleConfirmAgreements = async () => {
    if (!integration?.id) return;

    // Check all are confirmed
    const allConfirmed = Object.values(confirmations).every(v => v);
    if (!allConfirmed) {
      setError('Du må bekrefte alle avtaler for å aktivere integrasjonen');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await authenticatedFetch(`/api/xtramile/integrations/${integration.id}/confirm-agreements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(confirmations)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke bekrefte avtaler');
      }

      setSuccess('Avtaler bekreftet. Integrasjonen er nå aktiv.');
      await loadData();

    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const allAgreementsConfirmed = Object.values(confirmations).every(v => v);
  // Check if integration is fully configured and approved
  const isFullyConfigured = integration?.has_api_key &&
    integration?.agreement_accepted &&
    integration?.approved_by_forbund;

  // Load courses and mappings when integration is ready
  const loadCoursesAndMappings = async () => {
    if (!integration?.id || !user?.id) return;

    try {
      setLoadingCourses(true);

      const [coursesRes, mappingsRes, studyPlansRes] = await Promise.all([
        authenticatedFetch(`/api/xtramile/courses/${integration.id}`),
        authenticatedFetch(`/api/xtramile/mappings/${integration.id}`),
        authenticatedFetch('/api/study-plans/list', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id })
        })
      ]);

      const coursesData = await coursesRes.json();
      const mappingsData = await mappingsRes.json();
      const studyPlansData = await studyPlansRes.json();

      if (coursesData.success) {
        setCourses(coursesData.courses || []);
      }
      setMappings(mappingsData.mappings || []);
      // Filter for approved study plans only
      const approvedPlans = (studyPlansData.study_plans || []).filter(
        (p: StudyPlan) => p.status === 'approved'
      );
      setStudyPlans(approvedPlans);

    } catch (err) {
      console.error('Failed to load courses/mappings:', err);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Load courses when integration becomes fully configured
  useEffect(() => {
    if (isFullyConfigured && integration?.id) {
      loadCoursesAndMappings();
    }
  }, [isFullyConfigured, integration?.id]);

  const handleCreateMapping = async () => {
    if (!integration?.id || !selectedCourse || !selectedStudyPlan) return;

    const course = courses.find(c => c.id === selectedCourse);
    if (!course) return;

    try {
      setSavingMapping(true);
      setError('');

      const response = await authenticatedFetch('/api/xtramile/mappings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationId: integration.id,
          xtramileCourseId: course.id,
          xtramileCourseName: course.name,
          xtramileCourseUrl: course.url,
          studyPlanId: selectedStudyPlan
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke opprette mapping');
      }

      setSuccess('Kursmapping opprettet. Venter på godkjenning fra studieforbund.');
      setSelectedCourse('');
      setSelectedStudyPlan('');
      setShowMappingForm(false);
      await loadCoursesAndMappings();

    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingMapping(false);
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    const confirmed = await confirm({
      title: 'Slett mapping',
      message: 'Er du sikker på at du vil slette denne mappingen?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      setError('');

      const response = await authenticatedFetch(`/api/xtramile/mappings/${mappingId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke slette mapping');
      }

      setSuccess('Mapping slettet');
      await loadCoursesAndMappings();

    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-kf-primary" />
      </div>
    );
  }

  if (!user?.member_id) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-medium text-yellow-800">Ingen organisasjon tilknyttet</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Du må være tilknyttet en medlemsorganisasjon for å kunne sette opp integrasjoner.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* LMS Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveLms('xtramile')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'xtramile'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Link2 size={18} />
              Xtramile LMS
            </button>
            <button
              onClick={() => setActiveLms('moodle')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'moodle'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              Moodle LMS
            </button>
            <button
              onClick={() => setActiveLms('tutorlms')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'tutorlms'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              TutorLMS
            </button>
            <button
              onClick={() => setActiveLms('learndash')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeLms === 'learndash'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GraduationCap size={18} />
              LearnDash
            </button>
          </nav>
        </div>
      </div>

      {/* Moodle Integration */}
      {activeLms === 'moodle' && (
        <MoodleSettings />
      )}

      {/* TutorLMS Integration */}
      {activeLms === 'tutorlms' && (
        <TutorLMSSettings />
      )}

      {/* LearnDash Integration */}
      {activeLms === 'learndash' && (
        <LearnDashSettings />
      )}

      {/* Xtramile Integration Card */}
      {activeLms === 'xtramile' && (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Link2 className="text-blue-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Xtramile LMS</h2>
                <p className="text-sm text-gray-500">Integrasjon med Xtramile læringssystem</p>
              </div>
            </div>
            {isFullyConfigured ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <CheckCircle size={16} />
                Aktiv
              </span>
            ) : integration ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                <AlertCircle size={16} />
                Krever oppsett
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                Ikke konfigurert
              </span>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Error/Success messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          )}

          {/* Step 1: API Key - Only show when NOT fully configured */}
          {!isFullyConfigured && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                integration?.has_api_key ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {integration?.has_api_key ? <Check size={14} /> : '1'}
              </div>
              <h3 className="font-medium text-gray-900">API-nøkkel</h3>
            </div>

            {integration?.has_api_key ? (
              <div className="ml-8 space-y-3">
                <p className="text-sm text-green-600">API-nøkkel er konfigurert</p>

                {/* Connection test status */}
                {integration.connection_tested ? (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle size={16} />
                    <span>Tilkobling testet og verifisert</span>
                    {integration.connection_tested_at && (
                      <span className="text-gray-400 text-xs">
                        ({new Date(integration.connection_tested_at).toLocaleDateString('nb-NO')})
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-700">
                      Du må teste tilkoblingen før du kan bekrefte avtalene nedenfor.
                    </p>
                    {integration.connection_test_error && (
                      <p className="text-sm text-red-600 mt-1">
                        Siste feil: {integration.connection_test_error}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleTestConnection}
                    disabled={testing}
                    className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 disabled:opacity-50"
                  >
                    {testing ? 'Tester...' : 'Test tilkobling'}
                  </button>
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    {showApiKey ? 'Skjul' : 'Endre API-nøkkel'}
                  </button>
                </div>
              </div>
            ) : null}

            {(!integration?.has_api_key || showApiKey) && (
              <div className="ml-8 space-y-3">
                <p className="text-sm text-gray-600">
                  Legg inn API-nøkkelen du har fått fra Xtramile. Du finner denne under{' '}
                  <a
                    href="https://app.xtramile.no/integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    Integrasjoner i Xtramile
                    <ExternalLink size={14} />
                  </a>
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="XM-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-kf-primary"
                  />
                  <button
                    onClick={handleSaveApiKey}
                    disabled={saving || !apiKey.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-kf-primary rounded-lg hover:bg-kf-secondary disabled:opacity-50"
                  >
                    {saving ? 'Lagrer...' : 'Lagre'}
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Step 2: Agreement Confirmations */}
          {integration?.has_api_key && !integration?.agreement_confirmed && (
            <div className="space-y-4 border-t pt-6">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                  integration.connection_tested ? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  2
                </div>
                <h3 className={`font-medium ${integration.connection_tested ? 'text-gray-900' : 'text-gray-400'}`}>
                  Bekreft avtaler
                </h3>
              </div>

              {!integration.connection_tested ? (
                <div className="ml-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-500">
                    Du må teste tilkoblingen i steg 1 før du kan bekrefte avtalene.
                  </p>
                </div>
              ) : (
              <div className="ml-8 space-y-4">
                <p className="text-sm text-gray-600">
                  For å aktivere integrasjonen må du lese og bekrefte følgende avtaler:
                </p>

                {/* Data Processing Agreement */}
                {templates.xtramile_data_agreement && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-gray-900">{templates.xtramile_data_agreement.name}</h4>
                    </div>
                    <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                      <ReactMarkdown>{templates.xtramile_data_agreement.content}</ReactMarkdown>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmations.dataAgreement}
                          onChange={(e) => setConfirmations({ ...confirmations, dataAgreement: e.target.checked })}
                          className="w-4 h-4 text-kf-primary rounded focus:ring-kf-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">Jeg bekrefter at jeg har lest og forstått denne avtalen</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Quality Control */}
                {templates.xtramile_quality_control && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-gray-900">{templates.xtramile_quality_control.name}</h4>
                    </div>
                    <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                      <ReactMarkdown>{templates.xtramile_quality_control.content}</ReactMarkdown>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmations.qualityControl}
                          onChange={(e) => setConfirmations({ ...confirmations, qualityControl: e.target.checked })}
                          className="w-4 h-4 text-kf-primary rounded focus:ring-kf-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">Jeg bekrefter at jeg vil ivareta kvalitetskontroll</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Content Alignment */}
                {templates.xtramile_content_alignment && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-gray-900">{templates.xtramile_content_alignment.name}</h4>
                    </div>
                    <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                      <ReactMarkdown>{templates.xtramile_content_alignment.content}</ReactMarkdown>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmations.contentAlignment}
                          onChange={(e) => setConfirmations({ ...confirmations, contentAlignment: e.target.checked })}
                          className="w-4 h-4 text-kf-primary rounded focus:ring-kf-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">Jeg bekrefter at kursinnhold samsvarer med studieplaner</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Assessment Guidelines */}
                {templates.xtramile_assessment_guidelines && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-gray-900">{templates.xtramile_assessment_guidelines.name}</h4>
                    </div>
                    <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                      <ReactMarkdown>{templates.xtramile_assessment_guidelines.content}</ReactMarkdown>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmations.assessmentGuidelines}
                          onChange={(e) => setConfirmations({ ...confirmations, assessmentGuidelines: e.target.checked })}
                          className="w-4 h-4 text-kf-primary rounded focus:ring-kf-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">Jeg bekrefter at vurdering overholder retningslinjene</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Spot Check */}
                {templates.xtramile_spot_check && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-gray-900">{templates.xtramile_spot_check.name}</h4>
                    </div>
                    <div className="p-4 max-h-48 overflow-y-auto text-sm text-gray-700 prose prose-sm">
                      <ReactMarkdown>{templates.xtramile_spot_check.content}</ReactMarkdown>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmations.spotCheckAllowed}
                          onChange={(e) => setConfirmations({ ...confirmations, spotCheckAllowed: e.target.checked })}
                          className="w-4 h-4 text-kf-primary rounded focus:ring-kf-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">Jeg tillater stikkprøvekontroller</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Confirm Button */}
                <div className="pt-4">
                  <button
                    onClick={handleConfirmAgreements}
                    disabled={saving || !allAgreementsConfirmed}
                    className="w-full px-4 py-3 text-sm font-medium text-white bg-kf-primary rounded-lg hover:bg-kf-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Lagrer...' : 'Bekreft alle avtaler og aktiver integrasjon'}
                  </button>
                  {!allAgreementsConfirmed && (
                    <p className="text-sm text-gray-500 text-center mt-2">
                      Du må bekrefte alle avtaler ovenfor
                    </p>
                  )}
                </div>
              </div>
              )}
            </div>
          )}

          {/* Status when fully configured */}
          {isFullyConfigured && (
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium bg-green-100 text-green-700">
                  <Check size={14} />
                </div>
                <h3 className="font-medium text-gray-900">Integrasjon aktiv</h3>
              </div>

              <div className="ml-8 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Synkroniserte fullføringer</p>
                  <p className="text-2xl font-bold text-gray-900">{integration.total_synced_completions || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Utstedte attester</p>
                  <p className="text-2xl font-bold text-gray-900">{integration.total_certificates_issued || 0}</p>
                </div>
              </div>

              {integration.last_sync_at && (
                <p className="ml-8 text-sm text-gray-500">
                  Siste synkronisering: {new Date(integration.last_sync_at).toLocaleString('nb-NO')}
                  {integration.last_sync_status && ` (${integration.last_sync_status})`}
                </p>
              )}

              <div className="ml-8">
                <p className="text-sm text-gray-500 mb-3">
                  Avtaler bekreftet {integration.agreement_confirmed_at && new Date(integration.agreement_confirmed_at).toLocaleDateString('nb-NO')}
                </p>
                <div className="space-y-2">
                  {[
                    { key: 'xtramile_data_agreement', label: 'Databehandleravtale' },
                    { key: 'xtramile_quality_control', label: 'Kvalitetskontroll' },
                    { key: 'xtramile_content_alignment', label: 'Innholdssamsvar' },
                    { key: 'xtramile_assessment_guidelines', label: 'Vurderingsretningslinjer' },
                    { key: 'xtramile_spot_check', label: 'Stikkprøvekontroll' }
                  ].map(({ key, label }) => {
                    const isExpanded = expandedAgreements.includes(key);
                    const template = templates[key];
                    return (
                      <div key={key} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedAgreements(prev =>
                            isExpanded
                              ? prev.filter(k => k !== key)
                              : [...prev, key]
                          )}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <CheckCircle size={16} className="text-green-600" />
                            {label}
                          </span>
                          {isExpanded ? (
                            <ChevronUp size={18} className="text-gray-500" />
                          ) : (
                            <ChevronDown size={18} className="text-gray-500" />
                          )}
                        </button>
                        {isExpanded && template && (
                          <div className="px-4 py-3 text-sm text-gray-600 prose prose-sm max-w-none border-t bg-white">
                            <ReactMarkdown>{template.content}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Course Mappings - Only show when fully configured */}
          {isFullyConfigured && (
            <div className="border-t pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium bg-blue-100 text-blue-700">
                    <BookOpen size={14} />
                  </div>
                  <h3 className="font-medium text-gray-900">Kursmappinger</h3>
                </div>
                <button
                  onClick={() => {
                    setShowMappingForm(!showMappingForm);
                    if (!showMappingForm && courses.length === 0) {
                      loadCoursesAndMappings();
                    }
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-kf-primary rounded-lg hover:bg-kf-secondary flex items-center gap-1"
                >
                  <Plus size={16} />
                  Ny mapping
                </button>
              </div>

              <div className="ml-8 space-y-4">
                <p className="text-sm text-gray-600">
                  Koble Xtramile-kurs til godkjente studieplaner for automatisk utstedelse av kompetanseattester.
                </p>

                {/* New Mapping Form */}
                {showMappingForm && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <h4 className="font-medium text-blue-900">Opprett ny kursmapping</h4>

                    {loadingCourses ? (
                      <div className="flex items-center gap-2 text-sm text-blue-700">
                        <Loader2 className="animate-spin" size={16} />
                        Laster kurs fra Xtramile...
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Velg Xtramile-kurs
                          </label>
                          <select
                            value={selectedCourse}
                            onChange={(e) => setSelectedCourse(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-kf-primary"
                          >
                            <option value="">-- Velg kurs --</option>
                            {courses.filter(c => !c.isMapped).map(course => (
                              <option key={course.id} value={course.id}>
                                {course.name}
                              </option>
                            ))}
                          </select>
                          {courses.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              {courses.filter(c => !c.isMapped).length} av {courses.length} kurs tilgjengelig for mapping
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Velg studieplan
                          </label>
                          <select
                            value={selectedStudyPlan}
                            onChange={(e) => setSelectedStudyPlan(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-kf-primary"
                          >
                            <option value="">-- Velg studieplan --</option>
                            {studyPlans.map(plan => (
                              <option key={plan.id} value={plan.id}>
                                {plan.title}
                              </option>
                            ))}
                          </select>
                          {studyPlans.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              Ingen godkjente studieplaner tilgjengelig. Opprett og få godkjent en studieplan først.
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={handleCreateMapping}
                            disabled={savingMapping || !selectedCourse || !selectedStudyPlan}
                            className="px-4 py-2 text-sm font-medium text-white bg-kf-primary rounded-lg hover:bg-kf-secondary disabled:opacity-50"
                          >
                            {savingMapping ? 'Oppretter...' : 'Opprett mapping'}
                          </button>
                          <button
                            onClick={() => {
                              setShowMappingForm(false);
                              setSelectedCourse('');
                              setSelectedStudyPlan('');
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                          >
                            Avbryt
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Existing Mappings */}
                {mappings.length > 0 ? (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Eksisterende mappinger ({mappings.length})</h4>
                    {mappings.map(mapping => (
                      <div key={mapping.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{mapping.xtramile_course_name}</span>
                            <span className="text-gray-400">→</span>
                            <span className="text-gray-700 truncate">{mapping.study_plan_title}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              mapping.validation_status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : mapping.validation_status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {mapping.validation_status === 'approved' ? 'Godkjent' :
                               mapping.validation_status === 'rejected' ? 'Avvist' : 'Venter på godkjenning'}
                            </span>
                            {mapping.xtramile_course_url && (
                              <a
                                href={mapping.xtramile_course_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                Se kurs <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteMapping(mapping.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Slett mapping"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : !showMappingForm && (
                  <div className="text-center py-6 text-gray-500">
                    <BookOpen className="mx-auto mb-2 text-gray-300" size={32} />
                    <p className="text-sm">Ingen kursmappinger ennå</p>
                    <p className="text-xs text-gray-400 mt-1">Klikk "Ny mapping" for å koble et Xtramile-kurs til en studieplan</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Collapsible Configuration Section - Only show when fully configured */}
          {isFullyConfigured && (
            <div className="border-t pt-6">
              <button
                onClick={() => setShowConfigSection(!showConfigSection)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Settings size={20} className="text-gray-500" />
                  <span className="font-medium text-gray-700">Konfigurasjon</span>
                </div>
                {showConfigSection ? (
                  <ChevronUp size={20} className="text-gray-500" />
                ) : (
                  <ChevronDown size={20} className="text-gray-500" />
                )}
              </button>

              {showConfigSection && (
                <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg border">
                  <div className="space-y-3">
                    <p className="text-sm text-green-600">API-nøkkel er konfigurert</p>
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle size={16} />
                      <span>Tilkobling testet og verifisert</span>
                      {integration?.connection_tested_at && (
                        <span className="text-gray-400 text-xs">
                          ({new Date(integration.connection_tested_at).toLocaleDateString('nb-NO')})
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleTestConnection}
                      disabled={testing}
                      className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 disabled:opacity-50"
                    >
                      {testing ? 'Tester...' : 'Test tilkobling'}
                    </button>
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {showApiKey ? 'Skjul' : 'Endre API-nøkkel'}
                    </button>
                  </div>

                  {showApiKey && (
                    <div className="space-y-3 pt-4 border-t">
                      <p className="text-sm text-gray-600">
                        Legg inn ny API-nøkkel fra{' '}
                        <a
                          href="https://app.xtramile.no/integrations"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          Xtramile
                          <ExternalLink size={14} />
                        </a>
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="XM-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-kf-primary"
                        />
                        <button
                          onClick={handleSaveApiKey}
                          disabled={saving || !apiKey.trim()}
                          className="px-4 py-2 text-sm font-medium text-white bg-kf-primary rounded-lg hover:bg-kf-secondary disabled:opacity-50"
                        >
                          {saving ? 'Lagrer...' : 'Lagre'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
